import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, Plus, LogOut, Wifi, Loader2, Trash2, Link2, Bell, Edit,
  AlertTriangle, Hash, Package, Settings, Phone, ExternalLink, Save,
  Smartphone, Wallet, Key, Laptop, Watch, Headphones, X, Check, MessageCircle,
  Trophy
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAPTAG_LOGO = "https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG";

const objectTypes = [
  { value: 'elektronike', label: 'Elektronikë', icon: Laptop },
  { value: 'celesa', label: 'Çelësa', icon: Key },
  { value: 'portofol', label: 'Portofol', icon: Wallet },
  { value: 'telefon', label: 'Telefon', icon: Smartphone },
  { value: 'ore', label: 'Orë', icon: Watch },
  { value: 'kufje', label: 'Kufje', icon: Headphones },
  { value: 'tjeter', label: 'Tjetër', icon: Package }
];

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [objects, setObjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [editingObject, setEditingObject] = useState(null);
  
  // Add/Edit form state
  const [objectName, setObjectName] = useState('');
  const [objectDescription, setObjectDescription] = useState('');
  const [objectType, setObjectType] = useState('');
  const [finderMessage, setFinderMessage] = useState('');
  const [adding, setAdding] = useState(false);
  
  // Link sticker state
  const [stickerCode, setStickerCode] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    fetchObjects();
    fetchNotifications();
  }, []);

  const fetchObjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stickers`);
      setObjects(response.data);
    } catch (error) {
      console.error('Error fetching objects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API_URL}/api/dashboard/messages/${messageId}/read`);
      setNotifications(notifications.map(n => 
        n.id === messageId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleAddObject = async (e) => {
    e.preventDefault();
    
    if (!objectName || !objectType || !finderMessage) {
      toast.error('Plotëso të gjitha fushat e detyrueshme');
      return;
    }

    setAdding(true);
    try {
      if (editingObject) {
        // Update existing object
        await axios.put(`${API_URL}/api/stickers/${editingObject}`, {
          object_name: objectName,
          message: finderMessage
        });
        
        setObjects(objects.map(obj => 
          obj.id === editingObject 
            ? { ...obj, object_name: objectName, description: objectDescription, object_type: objectType, message: finderMessage } 
            : obj
        ));
        toast.success('Objekti u përditësua me sukses!');
        setEditingObject(null);
      } else {
        // Create new object
        const response = await axios.post(`${API_URL}/api/objects`, {
          object_name: objectName,
          description: objectDescription,
          object_type: objectType,
          message: finderMessage
        });
        
        setObjects([response.data, ...objects]);
        toast.success('Objekti u regjistrua me sukses! Tani mund ta lidhësh me stikerin.');
        setShowLinkForm(response.data.id);
      }
      
      setObjectName('');
      setObjectDescription('');
      setObjectType('');
      setFinderMessage('');
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving object:', error);
      toast.error('Gabim në ruajtjen e objektit');
    } finally {
      setAdding(false);
    }
  };

  const startEditObject = (obj) => {
    setEditingObject(obj.id);
    setObjectName(obj.object_name);
    setObjectDescription(obj.description || '');
    setObjectType(obj.object_type || '');
    setFinderMessage(obj.message);
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingObject(null);
    setObjectName('');
    setObjectDescription('');
    setObjectType('');
    setFinderMessage('');
    setShowAddForm(false);
  };

  const handleLinkSticker = async (objectId) => {
    if (!stickerCode) {
      toast.error('Shkruaj kodin e stikerit');
      return;
    }

    setLinking(true);
    try {
      await axios.put(`${API_URL}/api/objects/${objectId}/link`, {
        sticker_code: stickerCode.toUpperCase()
      });
      
      setObjects(objects.map(obj => 
        obj.id === objectId ? { ...obj, sticker_code: stickerCode.toUpperCase(), is_linked: true } : obj
      ));
      setStickerCode('');
      setShowLinkForm(null);
      toast.success('Objekti u lidh me stikerin me sukses!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Gabim në lidhjen e stikerit');
      }
    } finally {
      setLinking(false);
    }
  };

  const handleToggleLost = async (objectId, currentStatus) => {
    setUpdating(objectId);
    try {
      await axios.put(`${API_URL}/api/stickers/${objectId}`, {
        is_lost: !currentStatus
      });
      setObjects(objects.map(obj => 
        obj.id === objectId ? { ...obj, is_lost: !currentStatus } : obj
      ));
      toast.success(currentStatus ? 'Objekti u shënua si i gjetur' : 'Objekti u shënua si i humbur');
    } catch (error) {
      toast.error('Gabim');
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (objectId) => {
    if (!window.confirm('Je i sigurt që dëshiron të fshish këtë objekt?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/stickers/${objectId}`);
      setObjects(objects.filter(obj => obj.id !== objectId));
      toast.success('Objekti u fshi');
    } catch (error) {
      toast.error('Gabim');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getTypeIcon = (type) => {
    const found = objectTypes.find(t => t.value === type);
    return found ? found.icon : Package;
  };

  const getTypeLabel = (type) => {
    const found = objectTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getGoogleMapsLink = (lat, lng) => {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="floating-orb w-96 h-96 bg-violet-500/10 -top-48 -left-48 animate-float" />
        <div className="absolute inset-0 grid-pattern opacity-30" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src={TAPTAG_LOGO}
                alt="TAPTAG Logo"
                className="w-10 h-10 rounded-full object-cover shadow-lg"
              />
              <span className="text-xl font-bold text-slate-900">TapTag</span>
            </Link>
            
            <div className="flex items-center gap-4">
              {/* Rewards/Profile Link */}
              <Link to="/profile">
                <Button 
                  variant="ghost" 
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  data-testid="profile-rewards-btn"
                >
                  <Trophy className="w-5 h-5 mr-1" />
                  <span className="hidden sm:inline">Pikët</span>
                </Button>
              </Link>
              
              {/* Notifications Bell */}
              <div className="relative">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </div>
              
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Dil
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="fixed top-16 right-4 w-96 max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in-up">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-violet-600" />
              Njoftimet
            </h3>
            <button onClick={() => setShowNotifications(false)} className="p-1 hover:bg-slate-100 rounded">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          
          <div className="max-h-[calc(70vh-60px)] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Asnjë njoftim</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div 
                  key={notif.id}
                  className={`p-4 border-b border-slate-100 hover:bg-slate-50 ${!notif.is_read ? 'bg-violet-50/50' : ''}`}
                  onClick={() => !notif.is_read && markAsRead(notif.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${!notif.is_read ? 'bg-violet-100' : 'bg-slate-100'}`}>
                      <MessageCircle className={`w-5 h-5 ${!notif.is_read ? 'text-violet-600' : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">{notif.sender_name || 'Gjetës'}</span>
                        {!notif.is_read && (
                          <span className="w-2 h-2 bg-violet-500 rounded-full" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{notif.message}</p>
                      <p className="text-xs text-slate-500 mb-2">Objekti: {notif.object_name}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {notif.sender_phone && (
                          <a 
                            href={`tel:${notif.sender_phone}`}
                            className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full hover:bg-emerald-200"
                          >
                            <Phone className="w-3 h-3" />
                            {notif.sender_phone}
                          </a>
                        )}
                        {notif.latitude && notif.longitude && (
                          <a 
                            href={getGoogleMapsLink(notif.latitude, notif.longitude)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full hover:bg-violet-200"
                          >
                            <MapPin className="w-3 h-3" />
                            Shiko në Hartë
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <main className="pt-24 pb-12 px-4 relative z-10">
        <div className="container-app max-w-4xl">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-title">
              Mirësevini, <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">{user?.name}!</span>
            </h1>
            <p className="text-slate-600 mt-2">Menaxho objektet e tua të mbrojtura me TapTag</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-8">
            <Button
              onClick={() => {
                if (editingObject) {
                  cancelEdit();
                } else {
                  setShowAddForm(!showAddForm);
                }
              }}
              className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="add-object-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Shto Objekt
            </Button>
            <Link to="/shop">
              <Button variant="outline" className="rounded-xl border-2">
                <Wifi className="w-5 h-5 mr-2" />
                Bli Stikerë
              </Button>
            </Link>
          </div>

          {/* Add/Edit Object Form */}
          {showAddForm && (
            <Card className="border-0 shadow-xl mb-8 animate-fade-in-up overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  {editingObject ? <Edit className="w-5 h-5" /> : <Package className="w-5 h-5" />}
                  {editingObject ? 'Modifiko Objektin' : 'Regjistro Objekt të Ri'}
                </h2>
              </div>
              <CardContent className="p-6">
                <form onSubmit={handleAddObject} className="space-y-5">
                  {/* Object Name */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Emri i Objektit *</Label>
                    <Input
                      placeholder="P.sh. Çelësat e makinës"
                      value={objectName}
                      onChange={(e) => setObjectName(e.target.value)}
                      className="h-12 border-slate-300 focus:border-violet-500"
                      data-testid="object-name-input"
                    />
                  </div>

                  {/* Object Type */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Lloji i Objektit *</Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {objectTypes.map((type) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setObjectType(type.value)}
                            className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                              objectType === type.value 
                                ? 'border-violet-500 bg-violet-50 text-violet-700' 
                                : 'border-slate-200 hover:border-slate-300 text-slate-600'
                            }`}
                            data-testid={`type-btn-${type.value}`}
                          >
                            <Icon className="w-6 h-6" />
                            <span className="text-xs font-medium">{type.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Përshkrimi i shkurtër (opsional)</Label>
                    <Input
                      placeholder="P.sh. Çelësa me 3 çelësa dhe mbajtëse blu"
                      value={objectDescription}
                      onChange={(e) => setObjectDescription(e.target.value)}
                      className="h-12 border-slate-300 focus:border-violet-500"
                      data-testid="object-description-input"
                    />
                  </div>

                  {/* Message for Finder */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Mesazhi për Gjetësin *</Label>
                    <Textarea
                      placeholder="P.sh. Nëse i gjeni këto çelësa, ju lutem më kontaktoni. Faleminderit shumë!"
                      value={finderMessage}
                      onChange={(e) => setFinderMessage(e.target.value)}
                      className="min-h-[100px] border-slate-300 focus:border-violet-500"
                      data-testid="finder-message-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={adding}
                      className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl"
                      data-testid="submit-object-btn"
                    >
                      {adding ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          {editingObject ? 'Duke ruajtur...' : 'Duke regjistruar...'}
                        </>
                      ) : (
                        <>
                          {editingObject ? <Save className="w-4 h-4 mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                          {editingObject ? 'Ruaj Ndryshimet' : 'Regjistro Objektin'}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelEdit}
                      className="rounded-xl"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Anulo
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Objects List */}
          {objects.length === 0 ? (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-violet-500/10 flex items-center justify-center mx-auto mb-6">
                  <Package className="w-10 h-10 text-violet-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Asnjë objekt i regjistruar</h2>
                <p className="text-slate-600 mb-6">Regjistro objektin e parë për të filluar mbrojtjen</p>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl shadow-lg"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Shto Objekt
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {objects.map((obj) => {
                const TypeIcon = getTypeIcon(obj.object_type);
                
                return (
                  <Card 
                    key={obj.id} 
                    className={`border-0 shadow-lg hover:shadow-xl transition-all ${obj.is_lost ? 'ring-2 ring-orange-500' : ''}`}
                    data-testid={`object-card-${obj.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${obj.is_lost ? 'bg-orange-500/10' : 'bg-violet-500/10'}`}>
                            <TypeIcon className={`w-7 h-7 ${obj.is_lost ? 'text-orange-500' : 'text-violet-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="text-lg font-bold text-slate-900">{obj.object_name}</h3>
                              <Badge variant="secondary" className="text-xs">
                                {getTypeLabel(obj.object_type)}
                              </Badge>
                              {obj.is_lost && (
                                <Badge className="bg-orange-500/10 text-orange-600 border-0">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  I Humbur
                                </Badge>
                              )}
                            </div>
                            
                            {obj.description && (
                              <p className="text-sm text-slate-500 mb-2">{obj.description}</p>
                            )}
                            
                            <p className="text-slate-600 text-sm mb-3">{obj.message}</p>
                            
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              {obj.sticker_code ? (
                                <span className="flex items-center gap-1 text-emerald-600 font-medium">
                                  <Link2 className="w-4 h-4" />
                                  Lidhur: {obj.sticker_code}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-amber-600">
                                  <Hash className="w-4 h-4" />
                                  Pa stiker
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {obj.scan_count || 0} skanime
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 ml-18 md:ml-0">
                          {!obj.sticker_code && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowLinkForm(showLinkForm === obj.id ? null : obj.id)}
                              className="text-violet-600 border-violet-200 hover:bg-violet-50"
                              data-testid={`link-sticker-btn-${obj.id}`}
                            >
                              <Link2 className="w-4 h-4 mr-2" />
                              Lidh me Stikerin
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditObject(obj)}
                            className="text-slate-500 hover:text-violet-600"
                            data-testid={`edit-object-${obj.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={obj.is_lost}
                              onCheckedChange={() => handleToggleLost(obj.id, obj.is_lost)}
                              disabled={updating === obj.id}
                              data-testid={`toggle-lost-${obj.id}`}
                            />
                            <span className="text-sm text-slate-600">I Humbur</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-red-500"
                            onClick={() => handleDelete(obj.id)}
                            data-testid={`delete-object-${obj.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>

                      {/* Link Sticker Form */}
                      {showLinkForm === obj.id && (
                        <div className="mt-4 pt-4 border-t border-slate-200 animate-fade-in-up">
                          <div className="flex items-end gap-3">
                            <div className="flex-1">
                              <Label className="text-sm text-slate-600 mb-2 block">Kodi i Stikerit (6 karaktere)</Label>
                              <Input
                                placeholder="P.sh. ABC123"
                                value={stickerCode}
                                onChange={(e) => setStickerCode(e.target.value.toUpperCase().slice(0, 6))}
                                className="h-10 uppercase font-mono text-lg tracking-wider"
                                maxLength={6}
                                data-testid={`sticker-code-input-${obj.id}`}
                              />
                            </div>
                            <Button
                              onClick={() => handleLinkSticker(obj.id)}
                              disabled={linking}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-10"
                              data-testid={`confirm-link-btn-${obj.id}`}
                            >
                              {linking ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-2" />
                                  Lidh
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => setShowLinkForm(null)}
                              className="h-10"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Shkruaj kodin 6-karakterësh nga stikeri NFC (gjeneruar në Admin Panel)
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 bg-slate-900 relative z-10">
        <div className="container-app">
          <div className="flex items-center justify-center gap-4">
            <img 
              src={TAPTAG_LOGO}
              alt="TAPTAG Logo"
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-sm text-slate-400">TAPTAG © 2024</span>
            <Link to="/admin" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
              <Settings className="w-3 h-3" />
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserDashboard;
